import { useEffect, useState } from 'react';
import { useAdobeImsAuth } from './auth/useAdobeImsAuth';
import { Header } from './components/Header';
import { TabNav } from './components/TabNav';
import { FilterBar } from './components/FilterBar';
import { VPPanel } from './components/panels/VPPanel';
import { SDPanel } from './components/panels/SDPanel';
import { ODPanel } from './components/panels/ODPanel';
import { AvailabilityTrackerPanel } from './components/panels/AvailabilityTrackerPanel';
import { HeatmapModal } from './components/HeatmapModal';
import { useAppState } from './hooks/useAppState';
import { DealsProvider, useDeals } from './context/DealsContext';
import { fetchDeals, triggerSync } from './utils/database';
import type { TabState } from './types';
function friendlyTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const time = d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });
  if (d.toDateString() === now.toDateString()) return `Today at ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday at ${time}`;
  return `${d.toLocaleDateString([], {
    month: 'short',
    day: 'numeric'
  })} at ${time}`;
}
function Spinner({
  size = 16,
  color = '#6B7280'
}: {
  size?: number;
  color?: string;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" className="animate-spin" style={{
    color
  }} data-id="element-0">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" data-id="element-1" />
      <path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" data-id="element-2" />
    </svg>;
}
const ADOBE_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png';

// Inner component has access to DealsProvider context
function Dashboard() {
  const {
    accessLevel,
    error: authError,
    isLoading: isAuthLoading,
    isReady: isAuthReady,
    isSignedIn,
    signIn,
    signOut,
    user
  } = useAdobeImsAuth();
  const {
    state,
    currentTab,
    updateTab,
    switchTab,
    drillRegion
  } = useAppState();
  const {
    deals,
    setDeals,
    setSyncMeta,
    syncStatus,
    lastSynced,
    isLiveData
  } = useDeals();
  const [syncError, setSyncErrorMsg] = useState<string | null>(null);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const canSync = accessLevel === 'editor' || accessLevel === 'admin';

  // Fetch live deal data once the user is authenticated and provisioned
  useEffect(() => {
    if (!isSignedIn || accessLevel === 'unregistered' || accessLevel === 'anonymous') return;
    setSyncMeta('loading');
    fetchDeals().then(data => {
      if (data && data.records.length > 0) {
        setDeals(data.records);
        setSyncMeta(data.stale ? 'error' : 'success', data.lastSyncedAt ?? undefined);
      } else {
        setSyncMeta('never');
      }
    }).catch(err => {
      console.error('fetchDeals failed:', err);
      setSyncMeta('error');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, accessLevel]);
  const handleManualSync = async () => {
    setSyncMeta('loading');
    setSyncErrorMsg(null);
    try {
      const result = await triggerSync();
      if (result) {
        if (result.records.length > 0) {
          setDeals(result.records);
        }
        setSyncMeta(result.stale ? 'error' : 'success', result.lastSyncedAt);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sync failed';
      setSyncErrorMsg(msg);
      setSyncMeta('error');
    }
  };
  if (!isAuthReady || isAuthLoading) {
    return <div className="flex h-screen items-center justify-center bg-slate-50 text-slate-500 font-sans" data-id="element-3">
        Initializing Adobe sign-in...
      </div>;
  }
  if (!isSignedIn) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-4">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-5">
          <div className="flex items-center gap-4" data-id="element-6">
            <img src={ADOBE_LOGO_URL} alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-7" />
            <div data-id="element-8">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-9">OnDemand Team Dashboard</h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-10">Sign in with Adobe ID</p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-11">
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-id="element-12">
              Dashboard data is available only after Adobe IMS login.
              <br data-id="element-13" /><br data-id="element-14" />
              <p data-id="element-15">
                1. Click Sign-In<br data-id="element-16" />
                2. Login using your company account<br data-id="element-17" />
                3. Choose profile: Adobe Inc
              </p>
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-18">
                {authError}
              </div>}

            <button onClick={() => void signIn()} disabled={isAuthLoading} className="w-full flex items-center justify-center gap-2 rounded-xl bg-red-600 px-6 py-3 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60 transition-colors" data-id="element-19">
              {isAuthLoading ? 'Signing in…' : 'Sign in with Adobe ID'}
            </button>
          </div>
        </div>
      </div>;
  }
  if (accessLevel === 'unregistered') {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-20">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-21">
          <div className="flex items-center gap-4" data-id="element-22">
            <img src={ADOBE_LOGO_URL} alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-23" />
            <div data-id="element-24">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-25">OnDemand Team Dashboard</h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-26">Account not provisioned</p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-27">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-id="element-28">
              You are signed in with Adobe ID, but this account is not registered for dashboard
              access. No data is shown until an administrator assigns you a role in the system.
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-29">
                {authError}
              </div>}

            <button onClick={() => void signOut()} disabled={isAuthLoading} className="w-full flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 transition-colors" data-id="element-30">
              {isAuthLoading ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="min-h-screen" data-id="element-31">
      <Header user={user} accessLevel={accessLevel} isAuthLoading={isAuthLoading} onSignOut={() => void signOut()} data-id="element-32" />

      {/* Sync status bar — visible to all; sync button only for editor/admin */}
      <div className="flex items-center gap-3 px-4 py-1.5 bg-slate-50 border-b border-slate-200 text-xs text-slate-500" data-id="element-33">
        <span className="flex items-center gap-1.5" data-id="element-34">
          {syncStatus === 'loading' && <><Spinner size={12} data-id="element-35" /><span data-id="element-36">Loading live data…</span></>}
          {syncStatus === 'success' && `✓ Data synced${lastSynced ? ` · ${friendlyTime(lastSynced)}` : ''}`}
          {syncStatus === 'error' && `⚠ Last sync had errors — showing cached data${lastSynced ? ` · ${friendlyTime(lastSynced)}` : ''}`}
          {syncStatus === 'never' && '— No live data yet; showing sample data'}
          {syncStatus === 'idle' && ''}
        </span>
        {syncError && <span className="text-red-600 ml-2" data-id="element-37">{syncError}</span>}
        <div className="ml-auto flex items-center gap-2" data-id="element-38">
          {isLiveData && <button onClick={() => setShowHeatmap(true)} className="px-3 py-1 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 transition-colors" data-id="element-39">
              Coverage Heatmap
            </button>}
          {canSync && <button onClick={() => void handleManualSync()} disabled={syncStatus === 'loading'} className="px-3 py-1 rounded-md bg-slate-700 text-white text-xs font-medium hover:bg-slate-800 disabled:opacity-50 transition-colors flex items-center gap-1.5" data-id="element-40">
              {syncStatus === 'loading' ? <><Spinner size={12} color="#fff" data-id="element-41" />Syncing…</> : 'Sync from Workfront'}
            </button>}
        </div>
      </div>

      {showHeatmap && <HeatmapModal deals={deals} onClose={() => setShowHeatmap(false)} data-id="element-42" />}

      <TabNav currentTab={currentTab} onSwitch={switchTab} data-id="element-43" />

      <div className="relative" data-id="element-44">
        {/* Initial page-load overlay — shown while data is being fetched for the first time */}
        {syncStatus === 'loading' && !isLiveData && <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/80 min-h-64" data-id="element-45">
            <Spinner size={36} color="#4F46E5" data-id="element-46" />
            <p className="text-sm text-slate-500" data-id="element-47">Loading dashboard data…</p>
          </div>}

        {currentTab === 'vp' && <>
            <FilterBar tab="vp" state={state.vp} onUpdate={u => updateTab('vp', u as Partial<TabState>)} data-id="element-48" />
            <VPPanel state={state.vp} onDrillRegion={drillRegion} data-id="element-49" />
          </>}
        {currentTab === 'sd' && <>
            <FilterBar tab="sd" state={state.sd} onUpdate={u => updateTab('sd', u as Partial<TabState>)} data-id="element-50" />
            <SDPanel state={state.sd} data-id="element-51" />
          </>}
        {currentTab === 'od' && <>
            <FilterBar tab="od" state={state.od} onUpdate={u => updateTab('od', u as Partial<TabState>)} data-id="element-52" />
            <ODPanel state={state.od} data-id="element-53" />
          </>}
        {currentTab === 'at' && <AvailabilityTrackerPanel data-id="element-54" />}
      </div>
    </div>;
}
export default function App() {
  return <DealsProvider data-id="element-55">
      <Dashboard data-id="element-56" />
    </DealsProvider>;
}